import { RefObject, useLayoutEffect, useRef } from "react"
import { canvasMaxZoom, canvasMinZoom } from "../../data/defaults/canvas.zoom.defaults"
import { setPreferencesAsync, setPreferencesFulfilled } from "../../store/slices/editor.slice"
import { useAppDispatch, useAppSelector, useAppStore } from "../useRedux"
import useWheelZoom, { ZoomAnchor } from "../useWheelZoom"

/** The node the zoom is applied to as `transform: scale()`, see Preview.tsx. */
const CANVAS_WRAPPER_SELECTOR = ".canvas-div-wrapper"

/** A zoom waiting to be re-anchored once React has laid the new zoom out. */
type PendingAnchor = ZoomAnchor & {
    /** the zoomed point, in the wrapper's own unscaled px */
    x: number
    y: number
}

const measureWrapper = (scrollElement: HTMLElement) => {
    const wrapper = scrollElement.querySelector<HTMLElement>(CANVAS_WRAPPER_SELECTOR)
    if (!wrapper || !wrapper.offsetWidth) return null
    const rect = wrapper.getBoundingClientRect()
    return { rect, scale: rect.width / wrapper.offsetWidth }
}

/**
 * Cmd/Ctrl + wheel and pinch zoom for the canvas, centred on the pointer the way Figma does
 * it — the point under the pointer stays under it — see useWheelZoom.
 *
 * The scroll correction can only be worked out once React has re-laid the canvas out at
 * the new zoom (the wrapper around it grows and shrinks with the zoom), so the zoomed point
 * is remembered and the scroll is fixed up in a layout effect, before anything is painted.
 *
 * @param scrollRef the scrollable canvas area
 */
const useCanvasWheelZoom = (scrollRef: RefObject<HTMLElement>) => {
    const dispatch = useAppDispatch()
    const store = useAppStore()
    const canvasZoom = useAppSelector((state) => state.editor.value.settings.canvasZoom)
    const pendingAnchor = useRef<PendingAnchor | null>(null)

    useWheelZoom({
        ref: scrollRef,
        min: canvasMinZoom,
        max: canvasMaxZoom,
        getZoom: () => store.getState().editor.value.settings.canvasZoom,
        onZoom: (zoom, anchor) => {
            const measured = scrollRef.current && measureWrapper(scrollRef.current)
            pendingAnchor.current = measured
                ? {
                      ...anchor,
                      x: (anchor.clientX - measured.rect.left) / measured.scale,
                      y: (anchor.clientY - measured.rect.top) / measured.scale,
                  }
                : null
            // the store only — persisting every frame would flood the backend
            dispatch(setPreferencesFulfilled({ canvasZoom: zoom }))
        },
        onZoomEnd: (zoom) => dispatch(setPreferencesAsync("canvasZoom", zoom)),
    })

    useLayoutEffect(() => {
        const anchor = pendingAnchor.current
        pendingAnchor.current = null
        const scrollElement = scrollRef.current
        if (!anchor || !scrollElement) return

        const measured = measureWrapper(scrollElement)
        if (!measured) return
        scrollElement.scrollLeft += measured.rect.left + anchor.x * measured.scale - anchor.clientX
        scrollElement.scrollTop += measured.rect.top + anchor.y * measured.scale - anchor.clientY
    }, [canvasZoom, scrollRef])
}

export default useCanvasWheelZoom
